import React, { useState } from 'react';
import { Button, Form, InputGroup, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import SystemAlert from '../../../../../components/modal/SystemAlert';
import { useOrderContext } from '../../../../../providers/OrderProvider';
import { useOrderDetailViewOnly } from './OrderDetailViewOnlyContext';
import { useAddOrderInvoicesMutation, useRemoveOrderInvoiceMutation } from '../../../../../slices/ordersApiSlice';
import { markLocalOrderMutation } from '../../../../../utils/order/orderLocalMutationGuard';
import EventModalMediaContent from '../../../../../components/EventModalMediaContent';

const pad2 = (n) => String(n).padStart(2, '0');

const formatDateTimeDisplay = (value) => {
	if (!value) return '-';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return '-';
	return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const formatDateTimeCopy = (value) => {
	if (!value) return '-';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return '-';
	return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

const OrderInvoicesDetail = ({ invoiceTooltipId = 'order-detail-invoice-toggle-tooltip' }) => {
	const { order, isOrderReady } = useOrderContext();
	const viewOnly = useOrderDetailViewOnly();
	const isApprovalRejected = order?.approvalStatus === 'reject';
	const hideMutations = isApprovalRejected || viewOnly;
	const actionDisabled = !isOrderReady || isApprovalRejected;
	const [showAddInput, setShowAddInput] = useState(false);
	const [addValue, setAddValue] = useState('');
	const [addOrderInvoices, { isLoading }] = useAddOrderInvoicesMutation();
	const [removeOrderInvoice, { isLoading: isDeleting }] = useRemoveOrderInvoiceMutation();
	const [deletingInvoiceNumber, setDeletingInvoiceNumber] = useState('');
	const [confirmInvoiceNumber, setConfirmInvoiceNumber] = useState('');

	const invoices = Array.isArray(order?.invoices)
		? order.invoices
				.map((item) => {
					if (item && typeof item === 'object' && item.invoiceNumber != null && item.invoiceNumber !== '') {
						return {
							invoiceNumber: String(item.invoiceNumber).trim(),
							createdAt: item.createdAt,
						};
					}
					return null;
				})
				.filter(Boolean)
		: [];

	const handleAddInvoices = async () => {
		const trimmed = addValue.trim();
		if (!order?._id || !trimmed) return;

		markLocalOrderMutation(order._id);
		await addOrderInvoices({ _id: order._id, invoiceNumbers: [trimmed] }).unwrap();
		setAddValue('');
		setShowAddInput(false);
	};

	const handleCopyAddedTime = async (createdAt) => {
		const text = formatDateTimeCopy(createdAt);
		if (text === '-') return;
		try {
			await navigator.clipboard.writeText(text);
			toast.success('添加时间已复制');
		} catch {
			toast.error('复制失败');
		}
	};

	const handleRemoveInvoice = async () => {
		if (!order?._id || !confirmInvoiceNumber) return;

		setDeletingInvoiceNumber(confirmInvoiceNumber);
		try {
			markLocalOrderMutation(order._id);
			await removeOrderInvoice({ _id: order._id, invoiceNumber: confirmInvoiceNumber }).unwrap();
			setConfirmInvoiceNumber('');
		} finally {
			setDeletingInvoiceNumber('');
		}
	};

	return (
		<EventModalMediaContent
			icon='file-invoice'
			heading='关联发票'
			headingAction={
				hideMutations ? null : (
				<OverlayTrigger placement='top' overlay={<Tooltip id={invoiceTooltipId}>{showAddInput ? '收起' : '补充发票号码'}</Tooltip>}>
					<Button size='sm' className='p-0 border-0 text-warning' variant='link' disabled={!isOrderReady} onClick={() => setShowAddInput((v) => !v)}>
						<FontAwesomeIcon icon={showAddInput ? 'xmark' : 'plus'} />
					</Button>
				</OverlayTrigger>
				)
			}
		>
			<div>
				{invoices.length === 0 && (
					<div className='text-muted mb-2'>
						<span className='small'>暂无关联发票</span>
					</div>
				)}
				{invoices.map((invoice, index) => {
					const addedTime = formatDateTimeDisplay(invoice.createdAt);
					const canCopyTime = addedTime !== '-';
					return (
						<div key={`${invoice.invoiceNumber}-${index}`} className='mb-2 d-flex align-items-center gap-2'>
							<span className='text-body'># {invoice.invoiceNumber}</span>
							<div className='ms-auto d-flex align-items-center gap-2 flex-shrink-0'>
								<OverlayTrigger placement='top' overlay={<Tooltip id={`invoice-added-time-${invoice.invoiceNumber}-${index}`}>点击复制添加时间</Tooltip>}>
									<span
										role={canCopyTime ? 'button' : undefined}
										tabIndex={canCopyTime ? 0 : undefined}
										className={`fs-11 text-600 text-nowrap mb-0${canCopyTime ? ' cursor-pointer' : ''}`}
										onClick={canCopyTime ? () => handleCopyAddedTime(invoice.createdAt) : undefined}
										onKeyDown={
											canCopyTime
												? (e) => {
														if (e.key === 'Enter' || e.key === ' ') {
															e.preventDefault();
															handleCopyAddedTime(invoice.createdAt);
														}
													}
												: undefined
										}
									>
										{addedTime}
									</span>
								</OverlayTrigger>
								{hideMutations ? null : (
								<Button
									size='sm'
									variant='link'
									className='p-0 fs-11 text-danger'
									onClick={() => setConfirmInvoiceNumber(invoice.invoiceNumber)}
									disabled={actionDisabled || (isDeleting && deletingInvoiceNumber === invoice.invoiceNumber)}
									title='删除'
								>
									{isDeleting && deletingInvoiceNumber === invoice.invoiceNumber ? <Spinner size='sm' animation='border' /> : <FontAwesomeIcon icon='trash' />}
								</Button>
								)}
							</div>
						</div>
					);
				})}
				<SystemAlert
					title='提示内容'
					bodyText={`你确定要删除发票编号「${confirmInvoiceNumber}」吗？`}
					cancelText='手滑了'
					confirmText='确定删除'
					confirmColor='danger'
					confirmWaiting={isDeleting}
					modalShow={Boolean(confirmInvoiceNumber)}
					setModalShow={() => !isDeleting && setConfirmInvoiceNumber('')}
					handleConfirm={handleRemoveInvoice}
				/>
				{showAddInput && (
					<InputGroup className='mt-2'>
						<Form.Label htmlFor='orderDetailInvoiceNumber' className='visually-hidden'>
							发票号码
						</Form.Label>
						<Form.Control
							id='orderDetailInvoiceNumber'
							name='orderDetailInvoiceNumber'
							size='sm'
							className='border-warning-subtle'
							placeholder='请输入发票号码'
							value={addValue}
							onChange={(e) => setAddValue(e.target.value)}
							disabled={actionDisabled || isLoading}
						/>
						<Button size='sm' className='fs-11' variant='outline-primary' onClick={handleAddInvoices} disabled={actionDisabled || isLoading || !addValue.trim()}>
							{isLoading ? '添加中...' : '添加'}
						</Button>
					</InputGroup>
				)}
			</div>
		</EventModalMediaContent>
	);
};

export default OrderInvoicesDetail;
